import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import Header from "../../components/header/Header";
import Footer from "../../components/footer/Footer";
import { MenuBar } from "../../components/Menu/Menu";
import { getCurrentMeeting, submitConcerns } from "../../store/actions/meeting.action";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import MeetingParticipantProfile from "../../components/users/MeetingParticipantProfile";

export default function Concerns() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { id } = useParams();
  const [menuOpen, setMenuOpen] = useState(false);
  const [concernsText, setConcernsText] = useState("");
  const [concernType, setConcernType] = useState("general");
  const token = localStorage.getItem("token");
  const mail = localStorage.getItem("mail");
  const currentMeeting = useSelector((state) => state.meeting.currentMeeting);
  const navigation = useNavigate();

  useEffect(() => {
    dispatch(getCurrentMeeting({ token, id }));
  }, [dispatch, token, mail, id]);

  const closeMenuBar = () => {
    setMenuOpen(false);
  };

  const openMenuBar = () => {
    setMenuOpen(true);
  };

  const submitConcernsHandler = () => {
    if (concernsText.length < 10) {
      toast.warn("Please provide more details about your concern (at least 10 characters)", {
        position: "bottom-right",
      });
      return;
    }

    // Format concern type for display
    const concernTypeLabels = {
      general: "General Concern",
      inappropriate_behavior: "Inappropriate Behavior",
      technical_issues: "Technical Issues",
      schedule_conflict: "Schedule Conflict",
      content_quality: "Content Quality",
      other: "Other"
    };

    const formattedConcerns = `Concern Type: ${concernTypeLabels[concernType]}\n\n${concernsText}`;

    dispatch(
      submitConcerns({
        token,
        meeting_id: id,
        concerns: formattedConcerns,
      })
    );
    navigation("/user/meetings");
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    const day = date.getUTCDate();
    const month = date.getUTCMonth() + 1; // Months are zero-indexed
    const year = date.getUTCFullYear();
    const hours = date.getUTCHours();
    const minutes = date.getUTCMinutes();

    const formattedDate = `${day}/${month}/${year}`;
    const formattedTime = `${hours}:${minutes < 10 ? "0" : ""}${minutes}`;

    return `${formattedDate} ${formattedTime}`;
  };

  return (
    <div className="max-w-screen-2xl mx-auto p-4 h-full">
      <Header openMenu={openMenuBar} />
      <>
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-20 justify-center items-center m-12">
          <div className="flex flex-col items-center mt-28">
            <MeetingParticipantProfile 
              meeting={currentMeeting} 
              t={t} 
              formatDateTime={formatDateTime}
              showMeetingDetails={true}
            />
          </div>
          <div className="flex flex-col items-center mt-28">
            <div className="flex flex-col rounded-[var(--radius)] shadow-lg bg-white w-[400px] h-auto md:w-[600px] lg:w-[700px] md:h-auto p-8">
              <div className="flex flex-col gap-4">
                <p className="text-center text-xl md:text-2xl xl:text-3xl font-semibold text-gray-900">
                  Report a Concern
                </p>
                <p className="text-center text-sm md:text-base text-gray-600">
                  Please share any concerns or issues you experienced during this meeting
                </p>
                
                <div className="flex flex-col mt-2">
                  <label className="text-lg font-semibold text-gray-900 mb-2">
                    Concern Type
                  </label>
                  <select
                    className="w-full p-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-red-400"
                    value={concernType}
                    onChange={(e) => setConcernType(e.target.value)}
                  >
                    <option value="general">General Concern</option>
                    <option value="inappropriate_behavior">Inappropriate Behavior</option>
                    <option value="technical_issues">Technical Issues</option>
                    <option value="schedule_conflict">Schedule Conflict</option>
                    <option value="content_quality">Content Quality</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="flex flex-col mt-2">
                  <label className="text-lg font-semibold text-gray-900 mb-2">
                    Describe Your Concern
                  </label>
                  <textarea
                    className="w-full h-40 p-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-red-400 resize-none"
                    placeholder="Please provide detailed information about your concern..."
                    value={concernsText}
                    onChange={(e) => setConcernsText(e.target.value)}
                  />
                </div>

                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mt-2">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-yellow-700">
                        Your concern will be reviewed by our team. We take all reports seriously and will investigate appropriately.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex justify-center mt-4 gap-4">
                  <button
                    className="bg-gray-400 hover:bg-gray-500 transition-colors w-[140px] h-[45px] md:w-[160px] md:h-[50px] text-lg md:text-xl font-medium text-white rounded-full"
                    onClick={() => navigation("/user/meetings")}
                  >
                    Cancel
                  </button>
                  <button
                    className="bg-red-600 hover:bg-red-700 transition-colors w-[140px] h-[45px] md:w-[160px] md:h-[50px] text-lg md:text-xl font-medium text-white rounded-full"
                    onClick={submitConcernsHandler}
                  >
                    Submit
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
      <Footer />
      <MenuBar open={menuOpen} close={closeMenuBar} />
    </div>
  );
}
